var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { metricsManager } from "./metrics/MetricsManager";
import { BasePeerManager } from "./BasePeerManager";
import isEqual from "lodash/isEqual";
import { Logger } from "./Logger";
import { AvStrategy } from "gather-common/dist/src/public/av";
import { tracker } from "./tracker/Tracker";
import { intersection } from "lodash";
import { VideoPerformanceStages, videoPerformanceTracker } from "./videoPerformance";
import { issueDetector } from "./metrics/WebRTCIssueDetector";
import { GatherEventEmitter } from "gather-common-including-video/dist/src/public/GatherEventEmitter";
import { PeerManagerEvent } from "./PeerManagerEvent";
import { LoggableMethodFactory } from "./log.decorator";
import { gatherVideoClientDataSource, metricsManager as globalsMetricsManager } from "./Globals";
import { clearVideoEventAttributes, setVideoEventAttributes } from "./EventsManager";
import { RTCStatsManager } from "./metrics/RTCStatsManager";
import { DEFAULT_VIDEO_CAPTURE_HEIGHT, DEFAULT_VIDEO_CAPTURE_WIDTH, } from "gather-video-common/dist/src/public/settings";
import { GVCInternalEvent, rootEventEmitter } from "./EventEmitter/GVCEventEmitter";
const LoggableMethod = LoggableMethodFactory("PeerStrategyManager");
export const UNSUBSCRIBE_TIMEOUT_MS = 2500;
export const DISALLOW_TIMEOUT_MS = 2500;
export class PeerStrategyManager extends GatherEventEmitter {
    currentStrategy;
    currentAvStrategy = AvStrategy.Base;
    internalEventEmitter = rootEventEmitter;
    internalEventEmitterCleanupFns = [];
    adaptiveStreams = false;
    bitrateManager = true;
    producedTracks = new Map();
    producePaused = new Map();
    userId = "";
    spaceId = "";
    writeableAllowed = {};
    metricListenersCleanupFns = [];
    get allowed() {
        return this.writeableAllowed;
    }
    disallowedTimeout = new Map();
    subscribed = {};
    unsubscribeTimeout = new Map();
    republishCleanupFns;
    rtcStatsManager = new RTCStatsManager(this.internalEventEmitter);
    localVideoCaptureResolution = {
        width: DEFAULT_VIDEO_CAPTURE_WIDTH,
        height: DEFAULT_VIDEO_CAPTURE_HEIGHT,
    };
    constructor() {
        super();
        Object.assign(window, { pm: this });
        this.republishCleanupFns = new Map();
        this.currentStrategy = new BasePeerManager(this.rtcStatsManager);
    }
    setStrategy(strategy) {
        return this.start(this.userId, this.spaceId, strategy);
    }
    async start(userId, spaceId, avStrategy) {
        this.userId = userId;
        this.spaceId = spaceId;
        setVideoEventAttributes({ spaceId, avStrategy });
        this.currentStrategy.destroy();
        this.clearInternalEventEmitterListeners();
        this.rtcStatsManager.destroy();
        this.currentAvStrategy = avStrategy;
        this.rtcStatsManager = new RTCStatsManager(this.internalEventEmitter);
        this.internalEventEmitterCleanupFns = [
            this.internalEventEmitter.addEventListener(GVCInternalEvent.QualityLimitation, (payload) => this.publishEvent(PeerManagerEvent.QualityLimitation, {
                ...payload,
                activeVideoElements: this.getActiveVideoElements(),
            })),
            this.internalEventEmitter.addEventListener(GVCInternalEvent.StuckConnecting, (id, state, badState) => {
                this.publishEvent(PeerManagerEvent.StuckConnecting, id, state, badState);
            }),
        ];
        issueDetector.startRef();
        this.clearInternalInfoListeners();
        this.addInternalInfoListeners();
        this.cleanUpStrategyListeners();
        this._cleanupMetricListeners();
        const newStrategy = gatherVideoClientDataSource
            .getAVStrategyMap()?.[this.currentAvStrategy]?.(this.rtcStatsManager);
        if (!newStrategy)
            throw "Not Implemented";
        this.currentStrategy = newStrategy;
        globalsMetricsManager.setAttribute("avStrategy", this.currentAvStrategy);
        tracker.setMetadata("avStrategy", this.currentAvStrategy);
        this.onAdapativeStreams();
        this.initializeStrategyListeners();
        this._addMetricListeners();
        this.publishEvent(PeerManagerEvent.StartedStategy, AvStrategy.Base);
        await this.currentStrategy.start(this.userId, this.spaceId, this.internalEventEmitter);
        this.publishEvent(PeerManagerEvent.StartedStategy, this.currentAvStrategy);
        for (const [kind, track] of this.producedTracks) {
            const pause = this.producePaused.get(kind) ?? true;
            this.produceStartIfNecessary(kind, track, pause);
        }
        for (const [id, tagSet] of Object.entries(this.subscribed)) {
            this.currentStrategy.subscribe(id, tagSet);
        }
        for (const id of Object.keys(this.allowed)) {
            this.currentStrategy.allow(id);
        }
        this.currentStrategy.setLocalVideoCaptureResolution(this.localVideoCaptureResolution.width, this.localVideoCaptureResolution.height);
        this.currentStrategy.setBitrateManager(this.bitrateManager);
    }
    _addMetricListeners() {
        metricsManager.vc.consumerState.getConnectionReport = (userId) => this.currentStrategy.getConnectionReport(userId);
        this.metricListenersCleanupFns = [
            () => (metricsManager.vc.consumerState.getConnectionReport = undefined),
        ];
    }
    _cleanupMetricListeners() {
        this.metricListenersCleanupFns.forEach((cleanupFn) => cleanupFn());
        this.metricListenersCleanupFns = [];
    }
    _disallowImpl = async (id) => {
        delete this.writeableAllowed[id];
        this.publishEvent(PeerManagerEvent.AllowedChange);
        this.currentStrategy.disallow(id);
        if (this.isProducingUnnecessary()) {
            Logger.log("[PeerManager] Producing is no longer necessary");
            for (const [kind, _] of this.producedTracks) {
                this.currentStrategy.produceEnd(kind);
            }
        }
    };
    scheduleDisallow = (id) => {
        if (this.disallowedTimeout.get(id))
            return;
        const timeout = setTimeout(() => {
            this.disallowedTimeout.delete(id);
            this._disallowImpl(id);
        }, DISALLOW_TIMEOUT_MS);
        this.disallowedTimeout.set(id, timeout);
    };
    cancelDisallow = (id) => {
        const timeout = this.disallowedTimeout.get(id);
        if (timeout) {
            clearTimeout(timeout);
            this.disallowedTimeout.delete(id);
        }
    };
    allow(id) {
        this.cancelDisallow(id);
        if (this.allowed[id] || id === this.userId)
            return;
        const wasProduceUnnecessary = this.isProducingUnnecessary();
        this.writeableAllowed[id] = true;
        this.publishEvent(PeerManagerEvent.AllowedChange);
        this.currentStrategy.allow(id);
        if (wasProduceUnnecessary) {
            for (const [kind, track] of this.producedTracks) {
                const pause = this.producePaused.get(kind);
                if (pause !== undefined) {
                    this.currentStrategy.produceStart(kind, track, pause);
                }
                else {
                    console.error(`could not get previous value of pause for produced kind ${kind}`);
                }
            }
        }
    }
    setEventAttribute(prefix, name, value) {
        if (this.rtcStatsManager) {
            this.rtcStatsManager.setEventAttribute(prefix, name, value);
        }
    }
    disallow(id, immediate = false) {
        if (!this.allowed[id])
            return;
        immediate ? this._disallowImpl(id) : this.scheduleDisallow(id);
    }
    requestServerDebug() {
        return this.currentStrategy.requestServerDebug();
    }
    debugInfo() {
        return this.currentStrategy.debugInfo();
    }
    getStrategy() {
        return this.currentStrategy;
    }
    isProducingUnnecessary() {
        return Object.keys(this.allowed).length === 0;
    }
    initializeStrategyListeners() {
        const republish = (e) => (...data) => {
            this.publishEvent(e, ...data);
        };
        Object.values(PeerManagerEvent).forEach((pmEvent) => {
            const removeHandler = this.currentStrategy.addEventListener(pmEvent, republish(pmEvent));
            this.republishCleanupFns.set(pmEvent, removeHandler);
        });
    }
    setAdaptiveStreams(active) {
        this.adaptiveStreams = active;
        this.onAdapativeStreams();
    }
    setAdaptiveStreamSettings(options) {
        this.currentStrategy.setAdaptiveStreamSettings(options);
    }
    onAdapativeStreams() {
        this.currentStrategy.setAdaptiveStream(this.adaptiveStreams);
    }
    cleanUpStrategyListeners() {
        this.republishCleanupFns.forEach((removeHandler) => removeHandler());
        this.republishCleanupFns = new Map();
    }
    clearInternalInfoListeners() {
        this.removeEventListener(PeerManagerEvent.Info, metricsManager.vc.consumerState.consumerInfoEvent);
        this.removeEventListener(PeerManagerEvent.Info, metricsManager.vc.qualityFulfillment.handleInfo);
        this.removeEventListener(PeerManagerEvent.Info, tracker.capturePlayerInfo);
    }
    addInternalInfoListeners() {
        this.addEventListener(PeerManagerEvent.Info, metricsManager.vc.consumerState.consumerInfoEvent);
        this.addEventListener(PeerManagerEvent.Info, metricsManager.vc.qualityFulfillment.handleInfo);
        this.addEventListener(PeerManagerEvent.Info, tracker.capturePlayerInfo);
    }
    clearInternalEventEmitterListeners() {
        this.internalEventEmitterCleanupFns.forEach((cleanupFn) => cleanupFn());
        this.internalEventEmitterCleanupFns = [];
    }
    async produceStart(kind, track, pause) {
        this.producedTracks.set(kind, track);
        this.producePaused.set(kind, pause);
        await this.produceStartIfNecessary(kind, track, pause);
    }
    async produceStartIfNecessary(kind, track, pause) {
        if (!this.isProducingUnnecessary()) {
            return this.currentStrategy.produceStart(kind, track, pause);
        }
        else if (kind === "audio") {
            return this.currentStrategy.produceStart(kind, track, true);
        }
    }
    produceEnd(kind) {
        this.producedTracks.delete(kind);
        this.producePaused.delete(kind);
        return this.currentStrategy.produceEnd(kind);
    }
    produceResume(kind) {
        this.producePaused.set(kind, false);
        return this.currentStrategy.produceResume(kind);
    }
    producePause(kind) {
        this.producePaused.set(kind, true);
        return this.currentStrategy.producePause(kind);
    }
    scheduleUnsubscribe = (id) => {
        this.enableRemoteTracksLocally(id, false);
        if (this.unsubscribeTimeout.get(id))
            return;
        const timeout = setTimeout(() => {
            this.unsubscribeTimeout.delete(id);
            delete this.subscribed[id];
            setVideoEventAttributes({
                numParticipants: Object.keys(this.subscribed).length,
            });
            this.currentStrategy.unsubscribe(id);
        }, UNSUBSCRIBE_TIMEOUT_MS);
        this.unsubscribeTimeout.set(id, timeout);
    };
    cancelUnsubscribe = (id) => {
        const timeout = this.unsubscribeTimeout.get(id);
        if (timeout) {
            this.enableRemoteTracksLocally(id, true);
            clearTimeout(timeout);
            this.unsubscribeTimeout.delete(id);
        }
    };
    subscribe(id, tagSet) {
        if (this.unsubscribeTimeout.get(id)) {
            const alreadySubscribed = this.subscribed[id] ?? new Set();
            const deletedTags = intersection([...alreadySubscribed], [...tagSet]);
            deletedTags.forEach((tag) => {
                videoPerformanceTracker.clearMarksAndMeasures(id, tag);
            });
        }
        this.cancelUnsubscribe(id);
        if (isEqual(this.subscribed[id], tagSet))
            return;
        const newTags = new Set([...tagSet].filter((tag) => !this.subscribed[id]?.has(tag)));
        newTags.forEach((tag) => {
            if (!this.subscribed[id]?.has(tag) && tagSet.has(tag)) {
                videoPerformanceTracker.mark(VideoPerformanceStages.PeerManagerSubscribe, id, tag);
            }
        });
        this.subscribed[id] = tagSet;
        setVideoEventAttributes({
            numParticipants: Object.keys(this.subscribed).length,
        });
        return this.currentStrategy.subscribe(id, tagSet);
    }
    unsubscribe(id) {
        if (!this.subscribed[id])
            return;
        this.scheduleUnsubscribe(id);
    }
    enableRemoteTracksLocally(id, state) {
        const info = this.currentStrategy.getInfo(id);
        if (!info)
            return;
        if (info.video) {
            Logger.info(`${state ? "enable" : "disable "} ${id}'s audio and video tracks`);
            info.video.setMute("consumer", "audio", !state);
            info.video.setMute("consumer", "video", !state);
        }
        if (info.screenVideo) {
            Logger.info(`${state ? "enable" : "disable "} ${id}'s screen audio and video tracks`);
            info.screenVideo.setMute("consumer", "audio", !state);
            info.screenVideo.setMute("consumer", "video", !state);
        }
    }
    setLocalVideoCaptureResolution(width, height) {
        this.localVideoCaptureResolution = { width, height };
        if (this.currentStrategy) {
            this.currentStrategy.setLocalVideoCaptureResolution(width, height);
        }
    }
    setIsAVInterruptible(isInterruptible) {
        this.currentStrategy.setIsAVInterruptible(isInterruptible);
    }
    destroy() {
        this.subscribed = {};
        this.writeableAllowed = {};
        this.publishEvent(PeerManagerEvent.AllowedChange);
        this.producedTracks = new Map();
        this.producePaused = new Map();
        this.clearInternalEventEmitterListeners();
        this.cleanUpStrategyListeners();
        this.clearInternalInfoListeners();
        this._cleanupMetricListeners();
        issueDetector.stopRef();
        this.rtcStatsManager.destroy();
        clearVideoEventAttributes();
        return this.currentStrategy.destroy();
    }
    setMaxConsumerLayers(id, tag, spatialLayer, temporalLayer) {
        return this.currentStrategy.setMaxConsumerLayers(id, tag, spatialLayer, temporalLayer);
    }
    setMute(id, kind, muted) {
        return this.currentStrategy.setMute(id, kind, muted);
    }
    setAllMuted(kind, muted, exceptedPlayer) {
        return this.currentStrategy.setAllMuted(kind, muted, exceptedPlayer);
    }
    setBitrateManager(enabled) {
        if (this.bitrateManager !== enabled) {
            this.bitrateManager = enabled;
            this.currentStrategy.setBitrateManager(enabled);
        }
    }
    getBitrateManager() {
        return this.bitrateManager;
    }
    getCurrentAVStrategy() {
        return this.currentAvStrategy;
    }
    addLatestSpeaker(id) {
        return this.currentStrategy.addLatestSpeaker(id);
    }
    simulateScenario(scenario, id, kind, paused) {
        this.currentStrategy.simulateScenario(scenario, id, kind, paused);
    }
    connectToServerIfNotConnected() {
        this.currentStrategy.connectToServerIfNotConnected();
    }
    getWebrtcConnectionInfo(id, extraParams) {
        return this.currentStrategy.getWebrtcConnectionInfo(id, extraParams);
    }
    startObserve(id, elementInfo, kind) {
        if (kind === "video") {
            metricsManager.vc.qualityFulfillment.trackElementInfo(id, elementInfo);
        }
        this.currentStrategy.startObserve(id, elementInfo, kind);
    }
    stopObserve(id, kind) {
        if (kind === "video") {
            metricsManager.vc.qualityFulfillment.stopTrackingElementInfo(id);
        }
        this.currentStrategy.stopObserve(id, kind);
    }
    getActiveVideoElements() {
        return this.currentStrategy.getActiveVideoElements();
    }
    _DEBUG_triggerCpuOverload(delayMs = 0) {
        setTimeout(() => {
            this.publishEvent(PeerManagerEvent.QualityLimitation, {
                reason: "cpu",
                cpuLimitedDuration: 20,
                cpuLimitedPercentage: 0.5,
                activeVideoElements: this.getActiveVideoElements(),
            });
        }, delayMs);
    }
    networkChanged() {
        this.currentStrategy.networkChanged();
    }
}
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "setStrategy", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "start", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "initializeStrategyListeners", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "setAdaptiveStreams", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "cleanUpStrategyListeners", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "clearInternalInfoListeners", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "addInternalInfoListeners", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "produceStart", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "subscribe", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "unsubscribe", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "enableRemoteTracksLocally", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "setLocalVideoCaptureResolution", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "setIsAVInterruptible", null);
__decorate([
    LoggableMethod
], PeerStrategyManager.prototype, "destroy", null);
//# sourceMappingURL=PeerStrategyManager.js.map